import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, GitCompare, Plus } from "lucide-react";
import { frameworkApi } from "@/api/framework";
import { DiffStat, QuestionCard, RequirementRow, TaskEvidenceChoices } from "./FrameworkEditorParts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", { dateStyle: "medium" })
    : "—";
const errorText = (e) => e?.message || "The request failed. Try again.";
const blankQuestion = (order = 0) => ({
  level: 1,
  theme: "General",
  order,
  weight: 1,
  question_text: "",
  help_text: "",
  sub_criteria: [],
  task_evidence_requirements: [],
});
const blankRequirement = (order = 0) => ({ level: 1, label: "", order });

export default function FrameworkVersion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { query, update, reset } = useAdminListQuery();
  const [version, setVersion] = useState(null);
  const [allVersions, setAllVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [reconcilePending, setReconcilePending] = useState(false);
  const controlsDisabled = !!busy || reconcilePending;
  const [meta, setMeta] = useState({
    label: "",
    notes: "",
    effective_from: "",
  });
  const [metaDirty, setMetaDirty] = useState(false);
  const [newQuestion, setNewQuestion] = useState(blankQuestion());
  const [newRequirement, setNewRequirement] = useState(blankRequirement());
  const [compareId, setCompareId] = useState("");
  const [compareQuery, setCompareQuery] = useState("");
  const [comparison, setComparison] = useState(null);
  const [questionTotal, setQuestionTotal] = useState(0);
  const [requirementTotal, setRequirementTotal] = useState(0);
  const [publishOpen, setPublishOpen] = useState(false);
  const requestGeneration = useRef(0);
  const actionGeneration = useRef(0);
  const actionInFlight = useRef(false);
  const reconcilePendingRef = useRef(null);
  const metaDirtyRef = useRef(false);
  const readOnly = version && version.status !== "draft";
  const dirtyEditors = useRef(new Set());
  const newQuestionDirty = useRef(false);
  const newRequirementDirty = useRef(false);
  const updateNewQuestion = (update) => {
    newQuestionDirty.current = true;
    setNewQuestion(update);
  };
  const updateNewRequirement = (update) => {
    newRequirementDirty.current = true;
    setNewRequirement(update);
  };
  const publishTrigger = useRef(null);
  const setEditorDirty = useCallback((key, dirty) => {
    if (dirty) dirtyEditors.current.add(key);
    else dirtyEditors.current.delete(key);
  }, []);
  const confirmDiscard = () =>
    !(
      metaDirtyRef.current ||
      dirtyEditors.current.size ||
      newQuestionDirty.current ||
      newRequirementDirty.current
    ) || window.confirm("Discard unsaved framework changes?");

  const routeIdRef = useRef(id);
  const load = useCallback(async () => {
    const token = ++requestGeneration.current;
    setLoading(true);
    setError("");
    try {
      const [row, questionPage, requirementPage] = await Promise.all([
        frameworkApi.metadata(id),
        frameworkApi.questions(id, { limit: "100", ...(query.question_q ? { q: query.question_q } : {}), ...(query.question_level ? { level: query.question_level } : {}), ...(query.question_offset ? { offset: query.question_offset } : {}) }),
        frameworkApi.requirements(id, { limit: "100", ...(query.requirement_q ? { q: query.requirement_q } : {}), ...(query.requirement_level ? { level: query.requirement_level } : {}), ...(query.requirement_offset ? { offset: query.requirement_offset } : {}) }),
      ]);
      if (token !== requestGeneration.current || routeIdRef.current !== id)
        return false;
      setVersion({ ...row, questions: questionPage.items || [], requirements: requirementPage.items || [] });
      setQuestionTotal(Number(questionPage.total) || 0);
      setRequirementTotal(Number(requirementPage.total) || 0);
      if (!metaDirtyRef.current)
        setMeta({
          label: row.label || "",
          notes: row.notes || "",
          effective_from: row.effective_from || "",
        });
    } catch (e) {
      if (token === requestGeneration.current && routeIdRef.current === id)
        setError(errorText(e));
      return false;
    } finally {
      if (token === requestGeneration.current && routeIdRef.current === id)
        setLoading(false);
    }
    return true;
  }, [id, query.question_q, query.question_level, query.question_offset, query.requirement_q, query.requirement_level, query.requirement_offset]);
  useEffect(() => {
    if (!version?.scheme) return undefined;
    let active = true;
    frameworkApi.list({ paged: "true", limit: "100", q: compareQuery || version.scheme })
      .then((result) => { if (active) setAllVersions(result?.items || []); })
      .catch(() => { if (active) setAllVersions([]); });
    return () => { active = false; };
  }, [version?.scheme, compareQuery]);

  useEffect(() => {
    routeIdRef.current = id;
    actionGeneration.current += 1;
    setBusy("");
    actionInFlight.current = false;
    reconcilePendingRef.current?.resolve(undefined);
    reconcilePendingRef.current = null;
    setLoading(true);
    setError("");
    setNotice("");
    setReconcilePending(false);
    metaDirtyRef.current = false;
    dirtyEditors.current.clear();
    newQuestionDirty.current = false;
    newRequirementDirty.current = false;
    setMetaDirty(false);
    setVersion(null);
    setComparison(null);
    setNewQuestion(blankQuestion());
    setNewRequirement(blankRequirement());
    setCompareId("");
    setCompareQuery("");
    setPublishOpen(false);
    return () => {
      requestGeneration.current += 1;
      actionGeneration.current += 1;
      reconcilePendingRef.current?.resolve(undefined);
      reconcilePendingRef.current = null;
    };
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  const mutate = async (label, fn, { refresh = true } = {}) => {
    if (actionInFlight.current || reconcilePendingRef.current) return undefined;
    actionInFlight.current = true;
    const token = ++actionGeneration.current;
    const routeId = routeIdRef.current;
    const current = () =>
      token === actionGeneration.current && routeIdRef.current === routeId;
    setBusy(label);
    setError("");
    setNotice("");
    try {
      const result = await fn();
      if (!current()) return undefined;
      if (!refresh) return current() ? result : undefined;
      if (await load()) return current() ? result : undefined;
      if (!current()) return undefined;
      // The write committed. Keep its caller pending until a successful read,
      // so retrying the read cannot submit the same create or save again.
      setBusy("");
      setReconcilePending(true);
      return await new Promise((resolve) => {
        reconcilePendingRef.current = { token, routeId, result, resolve };
      });
    } catch (e) {
      if (current()) setError(errorText(e));
      return undefined;
    } finally {
      if (current()) setBusy("");
      if (token === actionGeneration.current) actionInFlight.current = false;
    }
  };
  const questions = version?.questions || [];
  const requirements = version?.requirements || [];
  const questionsComplete = !query.question_q && !query.question_level && Number(query.question_offset || 0) === 0 && questions.length === questionTotal;
  const requirementsComplete = !query.requirement_q && !query.requirement_level && Number(query.requirement_offset || 0) === 0 && requirements.length === requirementTotal;
  const grouped = useMemo(
    () =>
      Object.entries(
        questions.reduce((all, q) => {
          const key = `${q.level}|${q.theme}`;
          (all[key] ||= []).push(q);
          return all;
        }, {}),
      ).sort(([a], [b]) => a.localeCompare(b)),
    [questions],
  );
  async function saveMeta(e) {
    e.preventDefault();
    const result = await mutate("meta", () =>
      frameworkApi.update(id, {
        ...meta,
        effective_from: meta.effective_from || null,
      }),
    );
    if (result) {
      metaDirtyRef.current = false;
      setMetaDirty(false);
    }
  }
  async function reconcileSavedForm() {
    const pending = reconcilePendingRef.current;
    if (!pending || pending.refreshing) return;
    pending.refreshing = true;
    setBusy("refresh");
    const refreshed = await load();
    // A response for A must not unlock an action now running on B.
    if (
      reconcilePendingRef.current !== pending ||
      pending.token !== actionGeneration.current ||
      pending.routeId !== routeIdRef.current
    )
      return;
    pending.refreshing = false;
    setBusy("");
    if (refreshed) {
      reconcilePendingRef.current = null;
      setReconcilePending(false);
      pending.resolve(pending.result);
    }
  }
  async function addQuestion(e) {
    e.preventDefault();
    const result = await mutate("question", () =>
      frameworkApi.createQuestion(id, {
        ...newQuestion,
        level: Number(newQuestion.level),
        order: Number(newQuestion.order),
        weight: Number(newQuestion.weight),
        sub_criteria: newQuestion.sub_criteria || [],
      }),
    );
    if (result) {
      newQuestionDirty.current = false;
      setNewQuestion(blankQuestion(questions.length + 1));
    }
  }
  async function addRequirement(e) {
    e.preventDefault();
    const result = await mutate("requirement", () =>
      frameworkApi.createRequirement(id, {
        ...newRequirement,
        level: Number(newRequirement.level),
        order: Number(newRequirement.order),
      }),
    );
    if (result) {
      newRequirementDirty.current = false;
      setNewRequirement(blankRequirement(requirements.length + 1));
    }
  }
  async function moveRequirement(requirementId, direction) {
    const ordered = [...requirements].sort((a, b) => a.order - b.order);
    const index = ordered.findIndex((r) => r.id === requirementId);
    const target = ordered[index + direction];
    if (!target) return;
    [ordered[index], ordered[index + direction]] = [
      ordered[index + direction],
      ordered[index],
    ];
    const result = await mutate("requirement-reorder", () =>
      frameworkApi.reorderRequirements(
        id,
        ordered.map((requirement, order) => ({ id: requirement.id, order })),
      ),
    );
    if (result)
      setNotice(`Requirement moved ${direction < 0 ? "up" : "down"}.`);
  }
  async function moveQuestion(questionId, group, direction) {
    const ordered = [...group];
    const index = ordered.findIndex((q) => q.id === questionId);
    const other = ordered[index + direction];
    if (!other) return;
    [ordered[index], ordered[index + direction]] = [
      ordered[index + direction],
      ordered[index],
    ];
    const result = await mutate("reorder", () =>
      frameworkApi.reorderQuestions(
        id,
        ordered.map((q, i) => ({ id: q.id, order: i })),
      ),
    );
    if (result) setNotice(`Question moved ${direction < 0 ? "up" : "down"}.`);
  }
  async function publish() {
    const result = await mutate("publish", () => frameworkApi.publish(id));
    if (result) {
      setPublishOpen(false);
      setNotice("Published. Existing assessment pins were preserved.");
    }
  }
  async function retire() {
    if (
      !window.confirm(
        `Retire v${version.version} “${version.label}”? Existing assessments remain pinned to this version.`,
      )
    )
      return;
    await mutate("retire", () => frameworkApi.retire(id));
  }
  async function removeVersion() {
    if (!confirmDiscard()) return;
    if (
      !window.confirm(
        `Delete draft v${version.version} “${version.label}”? This cannot be undone.`,
      )
    )
      return;
    const result = await mutate("delete", () => frameworkApi.remove(id), {
      refresh: false,
    });
    if (result) navigate("/admin/framework");
  }
  async function clone() {
    if (!confirmDiscard()) return;
    const created = await mutate("clone", () => frameworkApi.clone(id), {
      refresh: false,
    });
    if (created?.id) navigate(`/admin/framework/${created.id}`);
  }
  async function compare() {
    if (!compareId || compareId === id || actionInFlight.current) return;
    actionInFlight.current = true;
    const token = ++actionGeneration.current;
    setBusy("compare");
    setError("");
    try {
      const [diff, before] = await Promise.all([
        frameworkApi.diff(id, compareId),
        frameworkApi.get(compareId),
      ]);
      if (token !== actionGeneration.current) return;
      setComparison({ ...diff, before });
    } catch (e) {
      if (token === actionGeneration.current) setError(errorText(e));
    } finally {
      if (token === actionGeneration.current) setBusy("");
      if (token === actionGeneration.current) actionInFlight.current = false;
    }
  }
  if (loading && !version)
    return (
      <section
        className="framework-panel mx-auto w-full min-w-0 max-w-6xl"
        role="status"
      >
        Loading framework version…
      </section>
    );
  if (error && !version)
    return (
      <section className="framework-panel mx-auto w-full min-w-0 max-w-3xl">
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"
        >
          {error}
        </p>
        <button
          type="button"
          onClick={load}
          className="mt-4 min-h-11 rounded-lg border px-4 font-semibold"
        >
          Retry
        </button>
      </section>
    );
  if (!version) return null;
  return (
    <section
      className="framework-panel mx-auto w-full min-w-0 max-w-6xl space-y-6"
      aria-labelledby="version-title"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/admin/framework"
            onClick={(event) => {
              if (!confirmDiscard()) event.preventDefault();
            }}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-foreground hover:underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All frameworks
          </Link>
          <h1
            id="version-title"
            tabIndex={-1}
            className="mt-2 text-3xl font-bold text-foreground"
          >
            {version.label}{" "}
            <span className="text-xl font-medium text-muted-foreground">
              v{version.version}
            </span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {version.scheme} · {version.status} · effective{" "}
            {fmt(version.effective_from)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={clone}
            className="min-h-11 inline-flex items-center gap-2 rounded-lg border px-3 text-sm font-semibold"
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            Clone
          </button>
          {version.status === "draft" && (
            <>
              <button
                type="button"
                disabled={controlsDisabled || questions.length === 0}
                ref={publishTrigger}
                onClick={() => {
                  if (confirmDiscard()) setPublishOpen(true);
                }}
                className="min-h-11 inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                Publish
              </button>
              <button
                type="button"
                disabled={controlsDisabled}
                onClick={removeVersion}
                className="min-h-11 rounded-lg border border-red-200 px-3 text-sm font-semibold text-red-700 disabled:opacity-50"
              >
                Delete draft
              </button>
            </>
          )}
          {version.status === "published" && (
            <button
              type="button"
              disabled={controlsDisabled}
              onClick={retire}
              className="min-h-11 rounded-lg border border-red-200 px-3 text-sm font-semibold text-red-700 disabled:opacity-50"
            >
              Retire
            </button>
          )}
        </div>
      </div>
      {version.effective_from && version.effective_from > today() && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Available to publish on {fmt(version.effective_from)}. Publishing
          early is rejected; there is no automatic scheduler.
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900"
        >
          {notice}
        </p>
      )}
      {reconcilePending && (
        <p
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          The change was saved, but the editor could not refresh. Retry the
          refresh before making another change.
          <button
            type="button"
            onClick={reconcileSavedForm}
            disabled={!!busy}
            className="ml-3 min-h-11 rounded border border-amber-800 px-3 font-semibold"
          >
            Retry refresh
          </button>
        </p>
      )}
      <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold">
            Version details{" "}
            {readOnly && (
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                Read-only
              </span>
            )}
          </h2>
          <span className="text-xs text-muted-foreground">
            Published {fmt(version.published_at)} · retired{" "}
            {fmt(version.retired_at)}
          </span>
        </div>
        <form onSubmit={saveMeta} className="mt-4 grid gap-4 md:grid-cols-3">
          <label className="text-sm font-semibold">
            Label
            <input
              disabled={readOnly || controlsDisabled}
              value={meta.label}
              onChange={(e) => {
                metaDirtyRef.current = true;
                setMetaDirty(true);
                setMeta({ ...meta, label: e.target.value });
              }}
              className="mt-1 min-h-11 w-full rounded-lg border px-3 disabled:bg-muted"
            />
          </label>
          <label className="text-sm font-semibold">
            Effective from
            <input
              disabled={readOnly || controlsDisabled}
              type="date"
              value={meta.effective_from}
              onChange={(e) => {
                metaDirtyRef.current = true;
                setMetaDirty(true);
                setMeta({ ...meta, effective_from: e.target.value });
              }}
              className="mt-1 min-h-11 w-full rounded-lg border px-3 disabled:bg-muted"
            />
          </label>
          <label className="text-sm font-semibold md:col-span-1">
            Notes
            <textarea
              disabled={readOnly || controlsDisabled}
              value={meta.notes}
              onChange={(e) => {
                metaDirtyRef.current = true;
                setMetaDirty(true);
                setMeta({ ...meta, notes: e.target.value });
              }}
              rows={2}
              className="mt-1 w-full rounded-lg border p-3 disabled:bg-muted"
            />
          </label>
          {!readOnly && (
            <div className="md:col-span-3">
              <button
                disabled={controlsDisabled}
                type="submit"
                className="min-h-11 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Save details
              </button>
            </div>
          )}
        </form>
      </section>
      <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Compare versions</h2>
            <p className="text-sm text-muted-foreground">
              Comparison follows question lineage and direction.
            </p>
          </div>
          <div className="flex w-full min-w-0 flex-wrap gap-2 sm:w-auto">
            <input aria-label="Search comparison versions" value={compareQuery} onChange={(e) => setCompareQuery(e.target.value)} placeholder="Search version" className="min-h-11 min-w-0 rounded-lg border px-3" />
            <select
              aria-label="Version to compare against"
              disabled={controlsDisabled}
              value={compareId}
              onChange={(e) => {
                setCompareId(e.target.value);
                setComparison(null);
              }}
              className="min-h-11 min-w-0 max-w-full flex-1 rounded-lg border px-3"
            >
              <option value="">Choose a version</option>
              {allVersions
                .filter((v) => v.scheme === version.scheme && v.id !== id)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version} · {v.label}
                  </option>
                ))}
            </select>
            <button
              type="button"
              disabled={!compareId || controlsDisabled}
              onClick={compare}
              className="min-h-11 inline-flex items-center gap-2 rounded-lg border px-3 text-sm font-semibold"
            >
              <GitCompare className="h-4 w-4" aria-hidden="true" />
              Compare
            </button>
          </div>
        </div>
        {comparison && (
          <div className="mt-4">
            <p className="mb-3 text-sm text-muted-foreground">
              Showing <strong>v{version.version}</strong> against{" "}
              <strong>v{comparison.before?.version}</strong>; lineage is
              resolved by the server.
            </p>
            <div className="grid gap-3 sm:grid-cols-5">
              <DiffStat label="Added" count={comparison.added?.length || 0} />
              <DiffStat
                label="Removed"
                count={comparison.removed?.length || 0}
              />
              <DiffStat
                label="Changed"
                count={comparison.changed?.length || 0}
              />
              <DiffStat
                label="Unchanged"
                count={Math.max(
                  0,
                  (comparison.before?.questions?.length || 0) -
                    (comparison.removed?.length || 0) -
                    (comparison.changed?.length || 0),
                )}
              />
              <DiffStat
                label="Requirements changed"
                count={comparison.requirements?.changed?.length || 0}
              />
            </div>
          </div>
        )}
      </section>
      <section className="rounded-2xl border border-border bg-muted p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">
            Questions{" "}
            <span className="text-sm font-normal text-muted-foreground">
              ({questionTotal})
            </span>
          </h2>
          {readOnly && (
            <span className="text-sm text-muted-foreground">
              Published and retired questions cannot be edited.
            </span>
          )}
        </div>
        <AdminListToolbar search={query.question_q || ""} searchLabel="Search question text" total={questionTotal} loading={loading} onSearch={(q) => update({ question_q: q, question_offset: "" })} onReset={() => reset()}>
          <label className="text-sm font-medium">Level<select aria-label="Filter question level" value={query.question_level || "all"} onChange={(event) => update({ question_level: event.target.value, question_offset: "" })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All levels</option><option value="1">Level 1</option><option value="2">Level 2</option><option value="3">Level 3</option></select></label>
        </AdminListToolbar>
        {!questionsComplete && <p className="mt-3 text-sm text-muted-foreground">Question ordering is available only for the complete unfiltered list.</p>}
        <div className="mt-4 space-y-4">
          {grouped.map(([key, group]) => (
            <div key={key} className="space-y-3">
              <h3 className="text-sm font-bold text-foreground">
                Level {group[0].level} · {group[0].theme}
              </h3>
              {group.map((q, index) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  onDirtyChange={setEditorDirty}
                  index={index}
                  group={group}
                  readOnly={readOnly}
                  onSave={(qid, body) =>
                    mutate("question", () =>
                      frameworkApi.updateQuestion(id, qid, body),
                    )
                  }
                  onDelete={(qid) =>
                    mutate("question", () =>
                      frameworkApi.removeQuestion(id, qid),
                    )
                  }
                  onMove={moveQuestion}
                  globalBusy={controlsDisabled}
                  reorderDisabled={!questionsComplete}
                />
              ))}
            </div>
          ))}
        </div>
        <AdminListPager offset={Number(query.question_offset || 0)} limit={100} total={questionTotal} disabled={loading || Boolean(busy)} onPage={(offset) => update({ question_offset: offset }, { resetOffset: false })} />
        {!readOnly && (
          <form
            onSubmit={addQuestion}
            className="mt-5 rounded-xl border border-dashed border-teal-300 bg-muted p-4"
          >
            <h3 className="font-bold">Add question</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-4">
              <input
                required
                disabled={controlsDisabled}
                placeholder="Question text"
                value={newQuestion.question_text}
                onChange={(e) =>
                  updateNewQuestion({
                    ...newQuestion,
                    question_text: e.target.value,
                    task_evidence_requirements: [],
                  })
                }
                className="min-h-11 rounded-lg border px-3 md:col-span-2"
              />
              <input
                required
                disabled={controlsDisabled}
                placeholder="Theme"
                value={newQuestion.theme}
                onChange={(e) =>
                  updateNewQuestion({ ...newQuestion, theme: e.target.value })
                }
                className="min-h-11 rounded-lg border px-3"
              />
              <input
                required
                disabled={controlsDisabled}
                type="number"
                min="1"
                max="3"
                value={newQuestion.level}
                onChange={(e) =>
                  updateNewQuestion({ ...newQuestion, level: e.target.value })
                }
                aria-label="Question level"
                className="min-h-11 rounded-lg border px-3"
              />
            </div>
            <div className="mt-3"><TaskEvidenceChoices question={newQuestion} disabled={controlsDisabled} onChange={choices => updateNewQuestion({ ...newQuestion, task_evidence_requirements: choices })} /></div>
            <button
              disabled={controlsDisabled}
              type="submit"
              className="mt-3 min-h-11 inline-flex items-center gap-1 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add question
            </button>
          </form>
        )}
      </section>
      <section className="rounded-2xl border border-border bg-muted p-5">
        <h2 className="text-lg font-bold">
          Requirements{" "}
          <span className="text-sm font-normal text-muted-foreground">
              ({requirementTotal})
          </span>
        </h2>
        <AdminListToolbar search={query.requirement_q || ""} searchLabel="Search requirement label" total={requirementTotal} loading={loading} onSearch={(q) => update({ requirement_q: q, requirement_offset: "" })} onReset={() => reset()}>
          <label className="text-sm font-medium">Level<select aria-label="Filter requirement level" value={query.requirement_level || "all"} onChange={(event) => update({ requirement_level: event.target.value, requirement_offset: "" })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All levels</option><option value="1">Level 1</option><option value="2">Level 2</option><option value="3">Level 3</option></select></label>
        </AdminListToolbar>
        {!requirementsComplete && <p className="mt-3 text-sm text-muted-foreground">Requirement ordering is available only for the complete unfiltered list.</p>}
        <div className="mt-4 space-y-3">
          {requirements.map((r, index) => (
            <RequirementRow
              key={r.id}
              requirement={r}
              onDirtyChange={setEditorDirty}
              index={index}
              total={requirements.length}
              readOnly={readOnly}
              globalBusy={controlsDisabled}
              reorderDisabled={!requirementsComplete}
              onMove={moveRequirement}
              onSave={(body) =>
                mutate("requirement", () =>
                  frameworkApi.updateRequirement(id, r.id, body),
                )
              }
              onDelete={() =>
                mutate("requirement", () =>
                  frameworkApi.removeRequirement(id, r.id),
                )
              }
            />
          ))}
        </div>
        <AdminListPager offset={Number(query.requirement_offset || 0)} limit={100} total={requirementTotal} disabled={loading || Boolean(busy)} onPage={(offset) => update({ requirement_offset: offset }, { resetOffset: false })} />
        {!readOnly && (
          <form
            onSubmit={addRequirement}
            className="mt-5 flex flex-wrap gap-3 rounded-xl border border-dashed border-teal-300 bg-muted p-4"
          >
            <input
              required
              disabled={controlsDisabled}
              placeholder="Requirement label"
              value={newRequirement.label}
              onChange={(e) =>
                updateNewRequirement({ ...newRequirement, label: e.target.value })
              }
              className="min-h-11 min-w-0 w-full flex-1 rounded-lg border px-3 sm:min-w-[18rem] sm:w-auto"
            />
            <input
              required
              disabled={controlsDisabled}
              type="number"
              min="1"
              max="3"
              value={newRequirement.level}
              onChange={(e) =>
                updateNewRequirement({ ...newRequirement, level: e.target.value })
              }
              aria-label="Requirement level"
              className="min-h-11 w-24 rounded-lg border px-3"
            />
            <button
              disabled={controlsDisabled}
              type="submit"
              className="min-h-11 inline-flex items-center gap-1 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add requirement
            </button>
          </form>
        )}
      </section>
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent
          className="framework-panel"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            (
              publishTrigger.current || document.getElementById("version-title")
            )?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Publish framework v{version.version}</DialogTitle>
            <DialogDescription>
              This makes <strong>{version.label}</strong> the current published
              content for <strong>{version.scheme}</strong>. Existing
              assessments stay pinned to their saved version; new assessments
              use this publication.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Effective from: {fmt(version.effective_from)}. Future-dated versions
            remain drafts until that date and cannot be published early.
          </p>
          {version.effective_from && version.effective_from > today() && (
            <p
              role="alert"
              className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"
            >
              Publishing is unavailable before the effective date. The server
              will reject this request without changing the draft.
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
            >
              {error}
            </p>
          )}
          {reconcilePending && (
            <button
              type="button"
              disabled={!!busy}
              onClick={reconcileSavedForm}
              className="min-h-11 rounded-lg border px-4 font-semibold"
            >
              Retry refresh
            </button>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setPublishOpen(false)}
              className="min-h-11 rounded-lg border px-4 text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={controlsDisabled}
              onClick={publish}
              className="min-h-11 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy === "publish" ? "Publishing…" : "Publish version"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
